import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { LocalAiLeadRequestStatus } from "@/api/local-ai-lead-api"

import {
  approveLocalAiLeadRequest,
  denyLocalAiLeadRequest,
  getLocalAiLeadCompanyUsage,
  getLocalAiLeadCostBreakdown,
  getLocalAiLeadCosts,
  getLocalAiLeadMe,
  getLocalAiLeadRequests,
  getLocalAiLeadSummary,
  getLocalAiLeadTraining,
  getLocalAiLeadTrend,
  updateLocalAiLeadHeadcount,
} from "@/api/local-ai-lead-api"
import { useAuthGeneration } from "@/hooks/use-auth-generation"

const STALE_TIME = 60_000

export function useLocalAiLeadCompanyUsageQuery(
  dateRange: { startDate: string; endDate: string },
  groupValue?: string,
  enabled = true
) {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "companyUsage", dateRange, groupValue, authGeneration],
    queryFn: () => getLocalAiLeadCompanyUsage({ ...dateRange, groupValue: groupValue! }),
    staleTime: STALE_TIME,
    enabled: enabled && !!groupValue,
  })
}

export function useLocalAiLeadMeQuery() {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "me", authGeneration],
    queryFn: () => getLocalAiLeadMe(),
    staleTime: STALE_TIME,
  })
}

export function useLocalAiLeadCostsQuery(
  dateRange?: { range?: number; startDate?: string; endDate?: string },
  currency?: string,
  enabled = true
) {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "costs", dateRange, currency, authGeneration],
    queryFn: () =>
      getLocalAiLeadCosts({
        range: dateRange?.range,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        currency,
      }),
    staleTime: STALE_TIME,
    enabled,
  })
}

export function useLocalAiLeadHeadcountMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateLocalAiLeadHeadcount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["localAiLead", "costBreakdown"] })
    },
  })
}

export function useLocalAiLeadTrainingQuery(enabled = true) {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "training", authGeneration],
    queryFn: () => getLocalAiLeadTraining(),
    staleTime: STALE_TIME,
    enabled,
  })
}

export function useLocalAiLeadTrendQuery(
  dateRange?: { startDate?: string; endDate?: string },
  currency?: string,
  enabled = true
) {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "trend", dateRange, currency, authGeneration],
    queryFn: () =>
      getLocalAiLeadTrend({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        currency,
      }),
    staleTime: STALE_TIME,
    enabled,
  })
}

export function useLocalAiLeadCostBreakdownQuery(
  dateRange?: { startDate?: string; endDate?: string },
  options?: { currency?: string; groupValue?: string; enabled?: boolean }
) {
  const { currency, groupValue, enabled = true } = options ?? {}
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "costBreakdown", dateRange, currency, groupValue, authGeneration],
    queryFn: () =>
      getLocalAiLeadCostBreakdown({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        currency,
        groupValue,
      }),
    staleTime: STALE_TIME,
    enabled,
  })
}

export function useLocalAiLeadSummaryQuery(
  dateRange?: { startDate?: string; endDate?: string },
  currency?: string,
  enabled = true
) {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: ["localAiLead", "summary", dateRange, currency, authGeneration],
    queryFn: () =>
      getLocalAiLeadSummary({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        currency,
      }),
    staleTime: STALE_TIME,
    enabled,
  })
}

// =============================================================================
// Budget-request approvals (fair use round 2, #3087)
// =============================================================================

/**
 * Query key for the lead's request queue. The mutations invalidate by the
 * `["localAiLead", "requests"]` prefix, so the auth generation can sit last.
 */
export const localAiLeadRequestsKey = (
  status: LocalAiLeadRequestStatus,
  month: string | undefined,
  authGeneration: number
) => ["localAiLead", "requests", status, month ?? null, authGeneration] as const

/**
 * The lead's budget-request queue for a month, plus their allowance.
 *
 * Shorter stale time than the dashboard queries: this is a work queue two
 * people can be looking at (several leads may share one user, and an admin can
 * resolve the same request), so a stale "approve" button is worse here than an
 * extra fetch. The 409 `request_not_pending` is still the real guard.
 */
export function useLocalAiLeadRequestsQuery(
  status: LocalAiLeadRequestStatus = "pending",
  month?: string,
  options?: { enabled?: boolean }
) {
  const authGeneration = useAuthGeneration()
  return useQuery({
    queryKey: localAiLeadRequestsKey(status, month, authGeneration),
    queryFn: () => getLocalAiLeadRequests({ status, month }),
    staleTime: 15_000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Approve a request as the lead. Invalidates the whole `requests` branch (not
 * just this status) because the row moves between the pending and approved
 * lists, and the allowance that rides along with every list changes too.
 */
export function useApproveLocalAiLeadRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { requestId: string; blocks: number; month?: string }) =>
      approveLocalAiLeadRequest(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["localAiLead", "requests"] })
    },
  })
}

/** Deny a request as the lead. Same invalidation rationale as the approval. */
export function useDenyLocalAiLeadRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { requestId: string; reason?: string; month?: string }) =>
      denyLocalAiLeadRequest(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["localAiLead", "requests"] })
    },
  })
}
